import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { Text } from "@carbon6/c6-frontend-design";
import { Fragment, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import type { DiagnosticsStatus } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { HatTextTooltip } from "@/components/tooltip";
import { noticeChipClassName } from "@/shared/ui/emptyState/public";
import type { DeviationSeverity } from "./bandDeviation";
import { buildBandColumns, type BandSparkline } from "./bandSparkline";
import { referenceBandAbsenceText } from "./diagnosticsStatusText";
import {
  DIAGNOSTICS_CHART_CAPTION_WITHOUT_BAND,
  DIAGNOSTICS_GROUP_TERMS,
  DIAGNOSTICS_LEGEND_BAND_TEXT,
  DIAGNOSTICS_LEGEND_EPISODE_ITEMS,
  DIAGNOSTICS_LEGEND_EPISODE_LABEL,
  DIAGNOSTICS_PAPER_NOTE,
  DIAGNOSTICS_SEEK_HINT,
  DIAGNOSTICS_SHADING_NOTE,
  diagnosticsTermLabel,
  diagnosticsTermTip,
  type DiagnosticsLegendSwatch,
} from "./diagnosticsTerms";
import type { EpisodePhaseGrid, ReferenceBandGrid } from "./diagnosticsSeries";
import { GROUP_ORDER, type ChannelStructureIndex } from "./parquetChannels";
import type { PhaseSpan } from "./segments/segmentShading";
import styles from "./EpisodeParquetDeviationVisual.module.css";

// 스파크라인 viewBox. 폭 140 / 높이 34, 좌우 4·상하 4 여백이다(디자인과 같은 비율).
const VW = 140;
const VH = 34;
const PAD_X = 4;
const PAD_TOP = 4;
const PAD_BOTTOM = 4;

// 심각도를 색(c6 시맨틱 토큰)으로 바꾼다. ok=정상 / warn=주의 / alert=경고.
const SEVERITY_COLOR: Record<DeviationSeverity, string> = {
  ok: "var(--app-color-text-success)",
  warn: "var(--c6-color-text-warning)",
  alert: "var(--c6-color-text-danger)",
};
// 밴드 없는 채널(기준 밴드에 없음)의 에피소드 시리즈는 중립색으로 그린다.
const NEUTRAL_COLOR = "var(--c6-color-text-muted)";

// 컬렉션 상태별로, 밴드가 있어도 병기할 주의 문구. 문구가 없으면 배지를 표시하지 않는다.
const BAND_NOTICE_TEXT: Partial<Record<DiagnosticsStatus, string>> = {
  stale: "기준 통로가 최신 상태가 아닙니다.",
  partial: "일부 에피소드를 제외하고 집계했습니다.",
};

export type EpisodeParquetDeviationVisualProps = {
  grid: EpisodePhaseGrid;
  band: ReferenceBandGrid | null;
  // 컬렉션 진단 상태. 밴드 부재 사유와 stale/partial 병기에 쓴다. null 은 조회 실패를 뜻하며,
  // 그때는 사유 없이 숨긴다.
  bandStatus?: DiagnosticsStatus | null;
  // 컬렉션 산출 시각. 캡션에 "언제 잰 기준인가"를 병기한다. null 은 미산출 또는 조회 실패다.
  bandComputedAt?: string | null;
  // 진단 설명과 나란히 표시할 상단 조망 카드. 데이터셋 안에서 이 에피소드의 위치를 표시한다.
  // 스파크라인 렌더는 이 슬롯의 내용을 모른다.
  leadingCard?: ReactNode;
  // 데이터셋 구조 채널 색인. 그룹·표시명·상대 프레임의 SoT 다(hatchery-data → structure 응답).
  channels: ChannelStructureIndex;
  // 그래프의 한 지점(0~1 위상)을 누르면 호출한다. 영상을 그 장면으로 옮긴다. 이 값이 없으면
  // 그래프는 읽기 전용이다. 영상이 없는 자리에서도 같은 시각화를 쓰기 때문이다.
  onSeekToPhase?: (phase: number) => void;
  // 그래프 뒤 배경에 그리는 가로 구간(0~1 위상)과 색. 뜻은 호출부가 정한다(지금은 사람이 낸 구간).
  shading?: readonly PhaseSpan[];
  // 재생 위치 선을 그린다. 자리는 상위가 쓰는 CSS 변수 `--diag-playhead-phase` 가 정한다.
  showPlayhead?: boolean;
};

// 채널 밴드 보기의 본문. 기준 밴드(p10–p90 통로 + median) 위에 이 에피소드의 min–max
// 밴드와 mean 선을 겹친다. 회전각은 0~p90 반경과 대표 자세의 각도 선으로 비교한다.
// 기준 밖 비율은 색으로 강조한다. 기준이 없는 성분은 에피소드 시리즈만 중립색으로 그린다.
export function EpisodeParquetDeviationVisual({
  grid,
  band,
  bandStatus = null,
  bandComputedAt = null,
  leadingCard = null,
  channels,
  onSeekToPhase,
  shading = [],
  showPlayhead = false,
}: EpisodeParquetDeviationVisualProps): ReactNode {
  const columns = buildBandColumns(grid, band, channels);

  if (columns.length === 0) {
    return (
      <div className={styles.wrap}>
        <div className={styles.head}>
          <span className={styles.title}>PARQUET</span>
          <span className={noticeChipClassName}>표시할 채널이 없습니다.</span>
        </div>
      </div>
    );
  }

  const hasAnyBand = columns.some((col) => col.sparklines.some((s) => s.hasBand));
  const alertCount = columns.reduce(
    (n, col) => n + col.sparklines.filter((s) => s.hasBand && s.severity === "alert").length,
    0,
  );
  const bandNotice =
    hasAnyBand && bandStatus !== null ? (BAND_NOTICE_TEXT[bandStatus] ?? null) : null;
  const referenceEpisodes = band === null ? 0 : maxBandEpisodes(band);
  // 캡션 끝의 한 줄. 가로축 안내(누를 수 있을 때만)와 밴드 부재 사유를 괄호로 병기한다.
  // 산출 시각은 읽는 정보가 아니라 확인 용도다. 그래서 캡션이 아니라 블록 맨 아래 우측에 작게 둔다.
  const seekHint = onSeekToPhase === undefined ? "" : DIAGNOSTICS_SEEK_HINT;
  const shadingNote = shading.length > 0 || showPlayhead ? DIAGNOSTICS_SHADING_NOTE : "";
  const absenceText = hasAnyBand ? null : referenceBandAbsenceText(bandStatus);
  const captionTail = [seekHint, shadingNote, absenceText === null ? "" : `(${absenceText})`]
    .filter((part) => part !== "")
    .join(" ");

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <HatTextTooltip
          text={
            hasAnyBand ? diagnosticsTermTip("deviation") : DIAGNOSTICS_CHART_CAPTION_WITHOUT_BAND
          }
          placement="bottom"
          align="start"
        >
          <span className={styles.title}>
            {hasAnyBand ? diagnosticsTermLabel("deviation") : "에피소드 trajectory"}
          </span>
        </HatTextTooltip>
        <span className={styles.meta}>
          {columns.length}개 채널
          {hasAnyBand && referenceEpisodes > 0 ? ` · 기준 ${referenceEpisodes}개 에피소드` : ""}
        </span>
        {alertCount > 0 ? (
          <span className={styles.anomaly}>많이 다른 채널 {alertCount}개</span>
        ) : null}
        {bandNotice !== null ? <span className={noticeChipClassName}>{bandNotice}</span> : null}
      </div>
      {/* 상단 조망. 이 에피소드의 위치 카드와 진단 설명을 같은 행의 두 칸에 둔다. */}
      <div className={styles.overviewRow}>
        {leadingCard}
        <section className={styles.captionBlock} aria-label="진단 설명">
          <div className={styles.captionRow}>
            <Text variant={hatcheryTextModes["body-normal"]} className={styles.caption}>
              {DIAGNOSTICS_PAPER_NOTE}
            </Text>
          </div>
          <div className={styles.captionRow}>
            {hasAnyBand ? (
              <>
                <LegendItem swatch="band" text={DIAGNOSTICS_LEGEND_BAND_TEXT} />
                {/* 라벨은 소제목으로 한 번만 두고 세 색 구간을 한 줄씩 나눈다. */}
                <div className={styles.legendEpisodeRow}>
                  <Text
                    variant={hatcheryTextModes["body-normal"]}
                    className={[styles.caption, styles.legendEpisodeTitle].join(" ")}
                  >
                    {DIAGNOSTICS_LEGEND_EPISODE_LABEL}
                  </Text>
                  {DIAGNOSTICS_LEGEND_EPISODE_ITEMS.map((entry) => (
                    <LegendItem key={entry.swatch} swatch={entry.swatch} text={entry.text} />
                  ))}
                </div>
              </>
            ) : (
              <Text variant={hatcheryTextModes["body-normal"]} className={styles.caption}>
                {DIAGNOSTICS_CHART_CAPTION_WITHOUT_BAND}
              </Text>
            )}
            {captionTail !== "" ? (
              <Text variant={hatcheryTextModes["body-normal"]} className={styles.caption}>
                {captionTail}
              </Text>
            ) : null}
          </div>
        </section>
      </div>

      {/* 채널 카드는 그룹 머리글 아래로 묶는다. 제목은 topic 을 뗀 채널 이름이다. 그래서
          `action::ee_pose` 와 `observation_vector::ee_pose` 의 제목이 같다. 곡선 모양까지
          비슷하면 사용자는 같은 것이 두 번 그려졌다고 이해한다. 어느 그룹의 채널인지는 이
          머리글이 표시한다. */}
      <div className={styles.columns}>
        {GROUP_ORDER.map((group) => {
          const groupColumns = columns.filter((column) => column.group === group);
          if (groupColumns.length === 0) {
            return null;
          }
          const term = DIAGNOSTICS_GROUP_TERMS[group];
          return (
            <Fragment key={group}>
              <div className={styles.groupHead}>
                <HatTextTooltip text={term.tip} placement="bottom" align="start">
                  <span className={styles.groupTitle}>{term.label}</span>
                </HatTextTooltip>
                <span className={styles.groupCount}>{groupColumns.length}개 채널</span>
              </div>
              {groupColumns.map((column) => (
                <div key={column.field} className={styles.column}>
                  <div className={styles.columnHead}>
                    <span className={styles.columnTitle}>{column.title}</span>
                    {column.outsideFraction !== null ? (
                      <span className={styles.columnMax}>
                        다른 정도 {formatFraction(column.outsideFraction)}
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.dims}>
                    {column.sparklines.map((spark) => (
                      <Sparkline
                        key={spark.key}
                        spark={spark}
                        onSeekToPhase={onSeekToPhase}
                        shading={shading}
                        showPlayhead={showPlayhead}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </Fragment>
          );
        })}
      </div>
      {/* 산출 시각. 확인 용도이므로 눈에 띄지 않게 블록 맨 아래 우측에 둔다. */}
      {bandComputedAt !== null ? (
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.computedAt}>
          {`기준 ${formatDateTime(parseUtcMs(bandComputedAt))} 산출`}
        </Text>
      ) : null}
    </div>
  );
}

// 스와치를 색 클래스로 바꾼다. band 는 기준 통로의 회색이다. 나머지는 스파크라인의 심각도 색과 같다.
const LEGEND_SWATCH_CLASS: Record<DiagnosticsLegendSwatch, string> = {
  band: styles.swatchBand,
  ok: styles.swatchOk,
  warn: styles.swatchWarn,
  alert: styles.swatchAlert,
};

function LegendItem({
  swatch,
  text,
}: {
  swatch: DiagnosticsLegendSwatch;
  text: string;
}): ReactNode {
  return (
    <span className={styles.legendItem}>
      <span className={`${styles.swatch} ${LEGEND_SWATCH_CLASS[swatch]}`} aria-hidden="true" />
      <Text variant={hatcheryTextModes["body-normal"]} className={styles.caption}>
        {text}
      </Text>
    </span>
  );
}

function Sparkline({
  spark,
  onSeekToPhase,
  shading,
  showPlayhead,
}: {
  spark: BandSparkline;
  onSeekToPhase?: (phase: number) => void;
  shading: readonly PhaseSpan[];
  showPlayhead: boolean;
}): ReactNode {
  const color = spark.hasBand ? SEVERITY_COLOR[spark.severity] : NEUTRAL_COLOR;
  const xAt = (i: number, n: number): number =>
    PAD_X + (n <= 1 ? 0 : (i / (n - 1)) * (VW - 2 * PAD_X));
  const yAt = (v: number): number => {
    const span = spark.domainMax - spark.domainMin || 1;
    return VH - PAD_BOTTOM - ((v - spark.domainMin) / span) * (VH - PAD_TOP - PAD_BOTTOM);
  };

  // 가로 위치를 위상(0~1)으로 바꾼다. SVG 는 `preserveAspectRatio="none"` 이다. 그래서 뷰박스가
  // 아니라 실제 그려진 폭으로 계산해야 한다. 좌우 padding 은 데이터가 없는 여백이므로 빼고,
  // 나머지를 0~1 로 정규화한다.
  const seekFromPointer = (event: ReactMouseEvent<HTMLElement>): void => {
    if (onSeekToPhase === undefined) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }
    const padRatio = PAD_X / VW;
    const raw = (event.clientX - rect.left) / rect.width;
    const inner = (raw - padRatio) / Math.max(1 - 2 * padRatio, Number.EPSILON);
    onSeekToPhase(Math.min(1, Math.max(0, inner)));
  };

  const chart = (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      preserveAspectRatio="none"
      className={styles.svg}
      aria-hidden="true"
    >
      {/* 구간 음영. 곡선 뒤 배경이므로 맨 먼저 그린다. 여백을 덮지 않도록 좌우 padding 안쪽에 둔다. */}
      {shading.map((span) => (
        <rect
          key={span.id}
          x={xAtPhase(span.startPhase)}
          y={0}
          width={xAtPhase(span.endPhase) - xAtPhase(span.startPhase)}
          height={VH}
          fill={span.color}
          fillOpacity={0.16}
        />
      ))}
      {spark.hasBand ? (
        <>
          <path d={bandPath(spark.high, spark.low, xAt, yAt)} className={styles.band} />
          <path
            d={linePath(spark.median, xAt, yAt)}
            className={styles.median}
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
        </>
      ) : null}
      {/* 회전각은 대표 자세의 mean 만 있다. 성분 extrema 를 각도 범위로 읽지 않는다. */}
      {spark.kind === "component" ? (
        <path
          d={bandPath(spark.epMax, spark.epMin, xAt, yAt)}
          fill={color}
          fillOpacity={0.16}
          stroke={color}
          strokeWidth={1.2}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      {/* 이 에피소드의 mean 선. 기준 밖 판정의 대상이므로 밴드보다 진하게 그린다. */}
      <path
        d={linePath(spark.epMean, xAt, yAt)}
        fill="none"
        stroke={color}
        strokeWidth={1.2}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );

  return (
    <div className={styles.dim}>
      <div className={styles.dimHead}>
        <span className={styles.dimLabel}>{spark.label}</span>
        {spark.hasBand && spark.outsideFraction !== null ? (
          <span className={styles.dimFraction} style={{ color }}>
            다른 정도 {formatFraction(spark.outsideFraction)}
          </span>
        ) : null}
      </div>
      {spark.kind === "rotation" && !spark.hasBand ? (
        <span className={styles.muted}>비교할 대표 자세가 없습니다.</span>
      ) : null}
      <div className={styles.chartWrap}>
        {onSeekToPhase === undefined ? (
          chart
        ) : (
          // 그래프 자체가 표적이다. 누른 가로 위치가 이동할 지점이다. 별도 컨트롤을 두면 그 컨트롤은
          // 이동할 지점을 가리키지 못한다. 키보드 입력은 폭을 3등분해 앞·중간·끝으로 이동한다.
          <button
            type="button"
            className={styles.seekChart}
            aria-label={`${spark.label} — 그래프를 눌러 그 지점으로 영상을 옮깁니다.`}
            onClick={seekFromPointer}
          >
            {chart}
          </button>
        )}
        {showPlayhead ? <span className={styles.playhead} aria-hidden="true" /> : null}
      </div>
    </div>
  );
}

// 가로축 위상(0~1)을 viewBox x 로 바꾼다. 좌우 padding 은 데이터가 없는 여백이므로 그 안쪽에만 배치한다.
function xAtPhase(phase: number): number {
  return PAD_X + phase * (VW - 2 * PAD_X);
}

// 기준 밖 비율을 "N%" 로 바꾼다(정수 반올림).
function formatFraction(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

// 밴드 채널 중 최대 support(에피소드 수). 헤더의 "기준 N개 에피소드" 표기에 쓴다.
function maxBandEpisodes(band: ReferenceBandGrid): number {
  let max = 0;
  for (const channel of band.channels.values()) {
    if (channel.episodes > max) {
      max = channel.episodes;
    }
  }
  return max;
}

// 값 선 path. 비유한 값에서는 선을 끊는다.
function linePath(
  values: readonly number[],
  xAt: (i: number, n: number) => number,
  yAt: (v: number) => number,
): string {
  const n = values.length;
  let d = "";
  let pen = false;
  for (let i = 0; i < n; i += 1) {
    const v = values[i];
    if (!Number.isFinite(v)) {
      pen = false;
      continue;
    }
    d += `${pen ? "L" : "M"}${xAt(i, n).toFixed(1)} ${yAt(v).toFixed(1)} `;
    pen = true;
  }
  return d.trim();
}

// 상·하 두 계열로 닫은 밴드 폴리곤(upper 정방향 + lower 역방향). 한쪽이라도 비유한인 지점에서
// 폴리곤을 끊고, 다음 유한 구간에서 새 폴리곤을 시작한다. 표본이 모자란 지점을 이웃 값으로 채우지 않는다.
function bandPath(
  upper: readonly number[],
  lower: readonly number[],
  xAt: (i: number, n: number) => number,
  yAt: (v: number) => number,
): string {
  const n = Math.min(upper.length, lower.length);
  const pieces: string[] = [];
  let start = 0;
  while (start < n) {
    if (!Number.isFinite(upper[start]) || !Number.isFinite(lower[start])) {
      start += 1;
      continue;
    }
    let end = start;
    while (end + 1 < n && Number.isFinite(upper[end + 1]) && Number.isFinite(lower[end + 1])) {
      end += 1;
    }
    let d = "";
    for (let i = start; i <= end; i += 1) {
      d += `${i === start ? "M" : "L"}${xAt(i, n).toFixed(1)} ${yAt(upper[i]).toFixed(1)} `;
    }
    for (let i = end; i >= start; i -= 1) {
      d += `L${xAt(i, n).toFixed(1)} ${yAt(lower[i]).toFixed(1)} `;
    }
    pieces.push(`${d}Z`);
    start = end + 1;
  }
  return pieces.join(" ");
}
